import type { IStandardErrorResponse } from 'src/api/common';
import { useMutation, useQueryClient } from 'react-query';

import type {
    ICreatePlaybookResponse,
    INewPlaybook,
    LeadFilter,
    PlaybookAction
} from 'src/api/playbooks-api';
import type { IPlaybookV2 } from 'src/services/api/playbooks/types';
import { useNotifications } from 'src/notifications';
import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { playbooksPath } from 'src/services/api/playbooks/path';
import { getApiErrorMessage } from 'src/services/api/utils';

export type CreateTimeBasedPlaybookPayload = INewPlaybook & {
    lead_filters: LeadFilter[];
    primary_action: PlaybookAction.SendMessageResponse;
}

export type ICreatePlaybookRequest = INewPlaybook | CreateTimeBasedPlaybookPayload;
type Response = ICreatePlaybookResponse;

export const useCreatePlaybook = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<Response, IStandardErrorResponse, ICreatePlaybookRequest>(
        (payload) => {
            return apiClient(playbooksPath, {
                method: 'post',
                json: payload,
            }).json<Response>();
        }, {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.playbooks);
                queryClient.invalidateQueries(queryKeys.playbooksV2);
                queryClient.invalidateQueries(queryKeys.teamPlaybooks);
                queryClient.invalidateQueries(queryKeys.achievements);
                addNotification('Playbook created', 'success');
            },
            onError: (error) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to create playbook';
                addNotification(errorMessage, 'error');
            }
        }
    );
};

type ICreatePlaybookV2Request = {
    playbook: IPlaybookV2;
}

export const useCreatePlaybookV2 = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<IPlaybookV2, IStandardErrorResponse, ICreatePlaybookV2Request>(
        async (payload) => {
            const res = await apiClient('api/landlord/playbooks', {
                method: 'post',
                json: payload
            }).json<IPlaybookV2>();

            return res;
        }, {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.playbooks);
                queryClient.invalidateQueries(queryKeys.playbooksV2);
                queryClient.invalidateQueries(queryKeys.teamPlaybooks);
                queryClient.invalidateQueries(queryKeys.achievements);
                addNotification('Playbook created', 'success');
            },
            onError: (error) => {
                const errorMessage = getApiErrorMessage(error) || 'Failed to create playbook';
                addNotification(errorMessage, 'error');
            }
        }
    );
};
